import React, { useMemo } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import {
  StepHeader,
  StepCard,
  INPUT_CLASS,
  DROPDOWN_TRIGGER_CLASS,
} from '../components/StepLayout'

export default function Step4Filters({
  filterLogic,
  setFilterLogic,
  filterConditions = [],
  setFilterConditions,
  allFields = [],
  operators = [],
  initialPrefill = null,
}) {
  const fieldOptions = useMemo(
    () => allFields.map((f) => ({ value: f.key, label: `${f.label} (${f.module})` })),
    [allFields]
  )

  const operatorOptions = useMemo(
    () => operators.map((op) => ({ value: op.id, label: op.label })),
    [operators]
  )

  function addCondition() {
    const defaultField = allFields[0]?.key || ''
    setFilterConditions((prev) => [
      ...prev,
      {
        field: defaultField,
        operator: 'EQUALS',
        value: '',
        valueTo: '',
      },
    ])
  }

  function removeCondition(index) {
    setFilterConditions((prev) => prev.filter((_, i) => i !== index))
  }

  function updateCondition(index, prop, value) {
    setFilterConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [prop]: value } : c))
    )
  }

  function importHubFilters() {
    if (!initialPrefill?.filters) return
    const f = initialPrefill.filters
    const newConds = []

    if (f.status && f.status.length > 0) {
      newConds.push({
        field: 'project_status',
        operator: 'EQUALS',
        value: f.status[0],
        valueTo: '',
      })
    }
    if (f.dateFrom && f.dateTo) {
      newConds.push({
        field: 'project_start_date',
        operator: 'DATE_RANGE',
        value: f.dateFrom,
        valueTo: f.dateTo,
      })
    }
    if (newConds.length > 0) {
      setFilterConditions((prev) => [...prev, ...newConds])
    }
  }

  const headerActions = (
    <div className="flex items-center gap-2">
      {initialPrefill?.filters && (
        <button
          type="button"
          onClick={importHubFilters}
          className="h-8 text-xs font-bold text-primary border border-primary/30 px-2.5 rounded-[8px] hover:bg-primary/5 flex items-center gap-1 transition-colors"
        >
          <span className="material-symbols-outlined text-xs">filter_alt</span>
          Import Hub Filters
        </button>
      )}

      <div className="flex items-center gap-0.5 bg-surface-container-high p-0.5 rounded-[8px]">
        <button
          type="button"
          onClick={() => setFilterLogic('AND')}
          className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${
            filterLogic === 'AND'
              ? 'bg-primary text-on-primary shadow-xs'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          AND (All)
        </button>
        <button
          type="button"
          onClick={() => setFilterLogic('OR')}
          className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${
            filterLogic === 'OR'
              ? 'bg-primary text-on-primary shadow-xs'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          OR (Any)
        </button>
      </div>
    </div>
  )

  return (
    <div className="space-y-3 max-w-4xl mx-auto py-1">
      <StepHeader
        title="Dynamic Multi-Condition Filters"
        subtitle="Constrain data using parameterized condition blocks and logical evaluation."
        actions={headerActions}
      />

      <StepCard>
        {filterConditions.length === 0 ? (
          <div className="text-center py-8">
            <span className="material-symbols-outlined text-3xl text-slate-300 mb-1.5">filter_list_off</span>
            <p className="text-xs font-semibold text-on-surface mb-0.5">No filter conditions applied</p>
            <p className="text-[11px] text-on-surface-variant mb-3">
              All records matching your tenant access permissions will be included in the report.
            </p>
            <button
              type="button"
              onClick={addCondition}
              className="h-9 px-4 bg-primary text-on-primary rounded-[8px] text-xs font-bold shadow hover:opacity-90 inline-flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Add Filter Condition
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {filterConditions.map((cond, index) => {
              const selectedFieldObj = allFields.find((f) => f.key === cond.field)
              const isBetweenOrRange = cond.operator === 'BETWEEN' || cond.operator === 'DATE_RANGE'
              const isBlankCheck = cond.operator === 'IS_BLANK' || cond.operator === 'IS_NOT_BLANK'

              return (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center gap-2.5 p-3 bg-surface rounded-[8px] border border-surface-container-high"
                >
                  <span className="text-xs font-extrabold text-primary shrink-0 w-12 text-center sm:text-left">
                    {index === 0 ? 'WHERE' : filterLogic}
                  </span>

                  {/* Field Selector with AppDropdown */}
                  <div className="w-full sm:w-56 shrink-0">
                    <AppDropdown
                      value={cond.field}
                      onChange={(val) => updateCondition(index, 'field', val)}
                      options={fieldOptions}
                      triggerClassName={DROPDOWN_TRIGGER_CLASS}
                    />
                  </div>

                  {/* Operator Selector with AppDropdown */}
                  <div className="w-full sm:w-48 shrink-0">
                    <AppDropdown
                      value={cond.operator}
                      onChange={(val) => updateCondition(index, 'operator', val)}
                      options={operatorOptions}
                      triggerClassName={DROPDOWN_TRIGGER_CLASS}
                    />
                  </div>

                  {/* Value Input */}
                  {!isBlankCheck && (
                    <div className="flex-1 w-full flex items-center gap-2">
                      <input
                        type={selectedFieldObj?.dataType === 'date' ? 'date' : 'text'}
                        value={cond.value || ''}
                        onChange={(e) => updateCondition(index, 'value', e.target.value)}
                        placeholder="Value..."
                        className={INPUT_CLASS}
                      />

                      {isBetweenOrRange && (
                        <>
                          <span className="text-xs text-on-surface-variant font-bold shrink-0">to</span>
                          <input
                            type={selectedFieldObj?.dataType === 'date' ? 'date' : 'text'}
                            value={cond.valueTo || ''}
                            onChange={(e) => updateCondition(index, 'valueTo', e.target.value)}
                            placeholder="To..."
                            className={INPUT_CLASS}
                          />
                        </>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => removeCondition(index)}
                    className="p-2 text-slate-400 hover:text-error hover:bg-surface-container rounded-[8px] self-end sm:self-center shrink-0 transition-colors"
                    title="Remove Condition"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              )
            })}

            <button
              type="button"
              onClick={addCondition}
              className="h-8 text-xs font-bold text-primary hover:bg-primary/5 px-2.5 rounded-[8px] border border-primary/20 flex items-center gap-1 transition-colors mt-2"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Add Condition
            </button>
          </div>
        )}
      </StepCard>
    </div>
  )
}
